import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClockIcon,
  PlayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  UserIcon,
  BookOpenIcon,
  PencilSquareIcon,
  ClipboardDocumentCheckIcon,
  ArrowLeftIcon,
  AcademicCapIcon,
  DocumentArrowDownIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Text } from "@core/text";
import { Button } from "@core/button";
import { useNavigate } from "react-router-dom";
import { GradeOptions } from "@packages/constant/CourseData";

// Dynamic types based on API response
interface DynamicActivity {
  etkinlikId: string;
  baslik: string;
  emoji: string;
  aciklama: string;
  sure: number;
  tip: string;
  zorlukSeviyesi: string;
  gerekliMalzemeler?: string[];
  ubdOdak?: {
    baslik: string;
    aciklama: string;
    uygulama: string;
  };
  senaryolar?: Array<{
    baslik: string;
    emoji: string;
    durum: string;
    soru: string;
    odak: string;
    malzemeler?: string[];
    zorlukSeviyesi: string;
  }>;
  zamanRehberi?: Array<{
    zaman: string;
    baslik: string;
    emoji: string;
    ogrenciAksiyonlari?: string[];
    ogretmenAksiyonlari?: string[];
    ipucu?: string;
    dikkatNoktasi?: string;
  }>;
  soruBankası?: Array<{
    soru: string;
    kategori: string;
    zorluk: string;
    beklenenCevap?: string;
  }>;
  etkinlikDetaylari?: Array<{
    baslik: string;
    emoji: string;
    sure: string;
    aciklama: string;
    zorlukSeviyesi: string;
    adimlar?: string[];
    malzemeler?: string[];
    ipuclari?: string[];
  }>;
}

interface DynamicLessonData {
  courseType: string;
  lessonFlow: {
    konu: string;
    sinifSeviyesi: number;
    toplamSure: number;
    templateTipi: string;
    etkinlikler: DynamicActivity[];
  };
}

interface ClassicTeachingFlowProps {
  lessonData: DynamicLessonData;
  isLoading?: boolean;
}

const ClassicTeachingFlow: React.FC<ClassicTeachingFlowProps> = ({
  lessonData,
  isLoading = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );
  const [showOverview, setShowOverview] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <Text variant="h3">Ders adımları yükleniyor...</Text>
        </div>
      </div>
    );
  }

  // Safely access the activities from the dynamic data structure
  const activities: DynamicActivity[] =
    lessonData?.lessonFlow?.etkinlikler || [];

  // Get lesson info from dynamic data
  const lessonInfo = {
    konu: lessonData?.lessonFlow?.konu || "Ders",
    sinifSeviyesi: lessonData?.lessonFlow?.sinifSeviyesi || 0,
    toplamSure: lessonData?.lessonFlow?.toplamSure || 0,
  };

  const getStepColor = (tip: string) => {
    switch (tip) {
      case "giris":
      case "giriş":
        return "from-emerald-500 to-teal-600";
      case "kavram":
        return "from-blue-500 to-indigo-600";
      case "rehberli":
        return "from-purple-500 to-pink-600";
      case "bagimsiz":
      case "bağımsız":
        return "from-orange-500 to-red-600";
      case "degerlendirme":
      case "değerlendirme":
        return "from-red-500 to-pink-600";
      default:
        return "from-gray-400 to-gray-600";
    }
  };

  const getStepIcon = (tip: string) => {
    switch (tip) {
      case "giris":
      case "giriş":
        return PlayIcon;
      case "kavram":
        return BookOpenIcon;
      case "rehberli":
        return PencilSquareIcon;
      case "bagimsiz":
      case "bağımsız":
        return UserGroupIcon;
      case "degerlendirme":
      case "değerlendirme":
        return ClipboardDocumentCheckIcon;
      default:
        return AcademicCapIcon;
    }
  };

  const nextStep = () => {
    if (currentStep < activities.length - 1) {
      setSlideDirection("right");
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setSlideDirection("left");
      setCurrentStep(currentStep - 1);
    }
  };

  const slideVariants = {
    initial: (direction: string) => ({
      x: direction === "right" ? 300 : -300,
      opacity: 0,
    }),
    animate: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: string) => ({
      x: direction === "right" ? -300 : 300,
      opacity: 0,
    }),
  };

  const renderDynamicStep = (activity: DynamicActivity, index: number) => {
    const colorClass = getStepColor(activity.tip);

    return (
      <div className="w-full h-full flex flex-col">
        {/* Step Header */}
        <div
          className={`bg-gradient-to-r ${colorClass} p-2 px-8 text-white relative overflow-hidden`}
        >
          {/* Classic Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-4 text-6xl">📚</div>
            <div className="absolute bottom-4 left-4 text-4xl">📝</div>
            <div className="absolute top-1/2 right-1/3 text-3xl">🎓</div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-between flex-1">
                {/* Back Button */}
                <Button
                  variant="outline"
                  onClick={handleBackClick}
                  size="sm"
                  className="rounded-full flex items-center space-x-2 px-4 py-2 bg-white/20 border-white/30 text-white hover:bg-white/30"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>Geri</span>
                </Button>
                <div>
                  <div className="flex items-center justify-center space-x-3 mb-2">
                    {activity.emoji && (
                      <Text variant="span" className="text-4xl">
                        {activity.emoji}
                      </Text>
                    )}
                    <Text
                      variant="h1"
                      className="!text-white font-bold text-3xl"
                    >
                      Adım {index + 1}
                    </Text>
                    {activity.emoji && (
                      <Text variant="span" className="text-4xl">
                        {activity.emoji}
                      </Text>
                    )}
                  </div>
                  <Text variant="h2" className="!text-white/95 text-xl mb-2">
                    {activity.baslik}
                  </Text>
                  <div className="flex items-center justify-center space-x-4 text-white/90">
                    <div className="flex items-center space-x-1">
                      <ClockIcon className="w-4 h-4" />
                      <span>{activity.sure} dakika</span>
                    </div>
                    {activity.zorlukSeviyesi && (
                      <div className="flex items-center space-x-1">
                        <span>
                          {activity.zorlukSeviyesi.charAt(0).toUpperCase() +
                            activity.zorlukSeviyesi.slice(1)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step Progress Indicators */}
                <div className="flex items-center space-x-2">
                  {activities.map((_, stepIndex) => (
                    <button
                      key={stepIndex}
                      onClick={() => {
                        setSlideDirection(
                          stepIndex > currentStep ? "right" : "left",
                        );
                        setCurrentStep(stepIndex);
                      }}
                      className={`w-4 h-4 rounded-full transition-all duration-300 hover:scale-125 ${
                        stepIndex === currentStep
                          ? "bg-white scale-125"
                          : stepIndex < currentStep
                            ? "bg-green-300"
                            : "bg-white/30"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900/20 dark:via-blue-900/20 dark:to-indigo-900/20">
          <div className="max-w-7xl mx-auto p-4">
            {renderDynamicContent(activity, index)}
          </div>
        </div>
      </div>
    );
  };

  const renderDynamicContent = (activity: DynamicActivity, index: number) => {
    return (
      <div className="space-y-2">
        {/* Materials */}
        {activity.gerekliMalzemeler &&
          activity.gerekliMalzemeler.length > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-200 scale-[0.98] hover:scale-100 transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <AcademicCapIcon className="w-8 h-8 text-blue-600" />
                <Text variant="h3" className="text-blue-800 font-bold">
                  Gerekli Malzemeler
                </Text>
              </div>
              <div className="flex flex-wrap gap-2">
                {activity.gerekliMalzemeler.map((malzeme, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-100 dark:bg-blue-800/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium"
                  >
                    {malzeme}
                  </span>
                ))}
              </div>
            </div>
          )}

        {/* UBD Odak */}
        {activity.ubdOdak &&
          (activity.ubdOdak.baslik ||
            activity.ubdOdak.aciklama ||
            activity.ubdOdak.uygulama) && (
            <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 rounded-xl p-4 border border-red-200 scale-[0.98] hover:scale-100 transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <AcademicCapIcon className="w-8 h-8 text-red-600" />
                <Text variant="h3" className="text-red-800 font-bold">
                  UBD Odak: {activity.ubdOdak.baslik}
                </Text>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">📝</span>
                    <Text
                      variant="span"
                      className="text-red-900 font-semibold text-base"
                    >
                      Açıklama
                    </Text>
                  </div>
                  <Text
                    variant="span"
                    className="text-red-900/90 font-medium text-sm"
                  >
                    {activity.ubdOdak?.aciklama || (
                      <span className="text-gray-400">-</span>
                    )}
                  </Text>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">🔧</span>
                    <Text
                      variant="span"
                      className="text-red-900 font-semibold text-base"
                    >
                      Uygulama
                    </Text>
                  </div>
                  <Text
                    variant="span"
                    className="text-red-900/90 font-medium text-sm"
                  >
                    {activity.ubdOdak?.uygulama || (
                      <span className="text-gray-400">-</span>
                    )}
                  </Text>
                </div>
              </div>
            </div>
          )}

        {/* Scenarios */}
        {activity.senaryolar && activity.senaryolar.length > 0 && (
          <div className="bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-xl p-4 border border-green-200 scale-[0.98] hover:scale-100 transition-all duration-300">
            <Text variant="h3" className="text-green-800 font-bold mb-4">
              📋 Öğretim Senaryoları
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activity.senaryolar.map((senaryo, idx) => (
                <div
                  key={idx}
                  className="bg-white p-4 rounded-lg border border-green-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    {senaryo.emoji && (
                      <span className="text-2xl">{senaryo.emoji}</span>
                    )}
                    <Text variant="h4" className="font-semibold text-green-800">
                      {senaryo.baslik}
                    </Text>
                  </div>
                  {/* Zorluk Seviyesi */}
                  {senaryo.zorlukSeviyesi && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-yellow-500 text-lg">⭐</span>
                      <span className="font-semibold text-yellow-700 text-xs">
                        Zorluk Seviyesi:
                      </span>
                      <span className="text-yellow-800 text-xs font-medium px-2 py-0.5 rounded bg-yellow-50 border border-yellow-200">
                        {senaryo.zorlukSeviyesi.charAt(0).toUpperCase() +
                          senaryo.zorlukSeviyesi.slice(1)}
                      </span>
                    </div>
                  )}
                  <Text variant="p" size="sm" className="text-gray-700 mb-2">
                    <strong>Durum:</strong> {senaryo.durum}
                  </Text>
                  <Text variant="p" size="sm" className="text-gray-700 mb-2">
                    <strong>Soru:</strong> {senaryo.soru}
                  </Text>
                  {/* Malzemeler */}
                  {senaryo.malzemeler && senaryo.malzemeler.length > 0 && (
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-green-700 text-lg">🧰</span>
                      <div>
                        <span className="font-semibold text-green-700 text-sm">
                          Malzemeler:
                        </span>
                        <ul className="list-disc list-inside text-green-800 text-xs mt-1 space-y-0.5">
                          {senaryo.malzemeler.map((malzeme, mIdx) => (
                            <li key={mIdx}>
                              {malzeme.charAt(0).toUpperCase() +
                                malzeme.slice(1)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  <div className="bg-green-50 p-2 rounded text-green-700 text-sm mb-2">
                    <strong>Odak:</strong> {senaryo.odak}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time Guide */}
        {activity.zamanRehberi && activity.zamanRehberi.length > 0 && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-purple-200 scale-[0.98] hover:scale-100 transition-all duration-300">
            <Text variant="h3" className="text-purple-800 font-bold mb-4">
              ⏰ Dakika Dakika Rehber
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activity.zamanRehberi.map((zaman, idx) => (
                <div
                  key={idx}
                  className="bg-white p-4 rounded-lg border border-purple-200"
                >
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex items-center space-x-2">
                      {zaman.emoji && (
                        <span className="text-xl">{zaman.emoji}</span>
                      )}
                      <Text
                        variant="span"
                        className="font-semibold text-purple-800"
                      >
                        {zaman.zaman}: {zaman.baslik}
                      </Text>
                    </div>
                  </div>

                  {/* Student Actions */}
                  {zaman.ogrenciAksiyonlari &&
                    zaman.ogrenciAksiyonlari.length > 0 && (
                      <div className="ml-12 mb-3">
                        <Text
                          variant="h5"
                          className="text-blue-700 font-medium mb-1 text-sm"
                        >
                          👥 Öğrenci Aksiyonları:
                        </Text>
                        <ul className="list-disc list-inside space-y-1">
                          {zaman.ogrenciAksiyonlari.map(
                            (aksiyon, actionIdx) => (
                              <li
                                key={actionIdx}
                                className="text-gray-600 text-sm"
                              >
                                {aksiyon}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                  {/* Teacher Actions */}
                  {zaman.ogretmenAksiyonlari &&
                    zaman.ogretmenAksiyonlari.length > 0 && (
                      <div className="ml-12 mb-3">
                        <Text
                          variant="h5"
                          className="text-green-700 font-medium mb-1 text-sm"
                        >
                          👨‍🏫 Öğretmen Aksiyonları:
                        </Text>
                        <ul className="list-disc list-inside space-y-1">
                          {zaman.ogretmenAksiyonlari.map(
                            (aksiyon, actionIdx) => (
                              <li
                                key={actionIdx}
                                className="text-gray-600 text-sm"
                              >
                                {aksiyon}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                  {/* Tips and Attention Points */}
                  {(zaman.ipucu || zaman.dikkatNoktasi) && (
                    <div className="ml-12 space-y-2">
                      {zaman.ipucu && (
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <Text
                            variant="p"
                            size="sm"
                            className="text-yellow-800 font-medium"
                          >
                            💡 <strong>İpucu:</strong> {zaman.ipucu}
                          </Text>
                        </div>
                      )}
                      {zaman.dikkatNoktasi && (
                        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                          <Text
                            variant="p"
                            size="sm"
                            className="text-orange-800 font-medium"
                          >
                            ⚠️ <strong>Dikkat:</strong> {zaman.dikkatNoktasi}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Question Bank */}
        {activity.soruBankası && activity.soruBankası.length > 0 && (
          <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4 border border-orange-200 scale-[0.98] hover:scale-100 transition-all duration-300">
            <Text variant="h3" className="text-orange-800 font-bold mb-4">
              ❓ Soru Bankası
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activity.soruBankası.map((soru, idx) => (
                <div
                  key={idx}
                  className="bg-white p-4 rounded-lg border border-orange-200"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded">
                      {soru.zorluk.charAt(0).toUpperCase() +
                        soru.zorluk.slice(1)}
                    </span>
                    <Text
                      variant="span"
                      className="text-orange-700 font-medium text-sm"
                    >
                      {soru.kategori === "baslangic"
                        ? "Başlangıç"
                        : soru.kategori === "derinlestirme"
                          ? "Derinleştirme"
                          : soru.kategori === "baglanti"
                            ? "Bağlantı"
                            : soru.kategori}
                    </Text>
                  </div>
                  <Text variant="p" size="sm" className="text-gray-700 mb-2">
                    <strong>Soru:</strong> {soru.soru}
                  </Text>
                  {soru.beklenenCevap && (
                    <div className="bg-orange-50 p-2 rounded text-orange-700 text-sm">
                      <strong>Beklenen Cevap:</strong> {soru.beklenenCevap}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Details */}
        {activity.etkinlikDetaylari &&
          activity.etkinlikDetaylari.length > 0 && (
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-indigo-200 scale-[0.98] hover:scale-100 transition-all duration-300">
              <Text variant="h3" className="text-indigo-800 font-bold mb-4">
                📝 Etkinlik Detayları
              </Text>
              <div className="space-y-4">
                {activity.etkinlikDetaylari.map((detay, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-4 rounded-lg border border-indigo-200"
                  >
                    <div className="flex items-center space-x-2 mb-3">
                      {detay.emoji && (
                        <span className="text-2xl">{detay.emoji}</span>
                      )}
                      <Text
                        variant="h4"
                        className="font-semibold text-indigo-800"
                      >
                        {detay.baslik}
                      </Text>
                      <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded">
                        {detay.sure}
                      </span>
                      <span
                        className={
                          `text-xs px-2 py-1 rounded ` +
                          (detay.zorlukSeviyesi === "kolay"
                            ? "bg-green-100 text-green-700"
                            : detay.zorlukSeviyesi === "orta"
                              ? "bg-yellow-100 text-yellow-700"
                              : detay.zorlukSeviyesi === "zor"
                                ? "bg-red-100 text-red-700"
                                : "bg-indigo-100 text-indigo-700")
                        }
                      >
                        {detay.zorlukSeviyesi.charAt(0).toUpperCase() +
                          detay.zorlukSeviyesi.slice(1)}
                      </span>
                    </div>

                    <Text variant="p" size="sm" className="text-gray-700 mb-3">
                      {detay.aciklama}
                    </Text>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Steps */}
                      {detay.adimlar && detay.adimlar.length > 0 && (
                        <div className="bg-indigo-50 p-3 rounded-lg">
                          <Text
                            variant="span"
                            className="text-indigo-700 font-medium text-sm block mb-2"
                          >
                            📋 <strong>Adımlar:</strong>
                          </Text>
                          <ol className="list-decimal list-inside space-y-1 text-xs">
                            {detay.adimlar.map((adim, stepIdx) => (
                              <li key={stepIdx} className="text-indigo-600">
                                {adim}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {/* Materials */}
                      {detay.malzemeler && detay.malzemeler.length > 0 && (
                        <div className="bg-green-50 p-3 rounded-lg">
                          <Text
                            variant="span"
                            className="text-green-700 font-medium text-sm block mb-2"
                          >
                            🛠️ <strong>Malzemeler:</strong>
                          </Text>
                          <div className="flex flex-wrap gap-1">
                            {detay.malzemeler.map((malzeme, matIdx) => (
                              <span
                                key={matIdx}
                                className="bg-green-100 text-green-700 text-xs px-1 py-0.5 rounded"
                              >
                                {malzeme}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tips */}
                      {detay.ipuclari && detay.ipuclari.length > 0 && (
                        <div className="bg-yellow-50 p-3 rounded-lg">
                          <Text
                            variant="span"
                            className="text-yellow-700 font-medium text-sm block mb-2"
                          >
                            💡 <strong>İpuçları:</strong>
                          </Text>
                          <ul className="list-disc list-inside space-y-1 text-xs">
                            {detay.ipuclari.map((ipucu, tipIdx) => (
                              <li key={tipIdx} className="text-yellow-600">
                                {ipucu}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    );
  };

  const handleBackClick = () => {
    if (showOverview) {
      navigate(-1);
    } else {
      // Show overview instead of going back immediately
      setShowOverview(true);
    }
  };

  const handleActivityClick = (index: number) => {
    setCurrentStep(index);
    setShowOverview(false);
  };

  const renderOverview = () => {
    return (
      <div className="h-full flex flex-col">
        {/* Overview Header */}
        <div className="flex-shrink-0 p-6 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={handleBackClick}
                size="sm"
                className="rounded-full flex items-center space-x-2 px-4 py-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Geri</span>
              </Button>
              <div>
                <div className="flex items-center space-x-3">
                  <Text variant="h1" className="text-2xl font-bold">
                    📚 {t("courseType." + lessonData.courseType)} - Genel Bakış
                  </Text>
                  <AcademicCapIcon className="w-6 h-6 text-blue-500" />
                </div>
                <Text variant="p" status="secondary">
                  {
                    GradeOptions(t).find(
                      (a) => a.value == lessonInfo.sinifSeviyesi.toString(),
                    )?.label
                  }
                  {lessonInfo.sinifSeviyesi > 0 && ". Sınıf"} •{" "}
                  {lessonInfo.toplamSure} dakika • {activities.length} Etkinlik
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Overview Content */}
        <div className="flex-1 min-h-0 p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activities.map((activity, index) => {
                const colorClass = getStepColor(activity.tip);
                const StepIcon = getStepIcon(activity.tip);

                return (
                  <motion.div
                    key={activity.etkinlikId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
                  >
                    {/* Card Header */}
                    <div
                      className={`bg-gradient-to-r ${colorClass} p-6 text-white relative overflow-hidden`}
                    >
                      <div className="absolute top-2 right-2 text-3xl opacity-20">
                        {activity.emoji}
                      </div>
                      <div className="relative z-10">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                            <StepIcon className="w-6 h-6" />
                          </div>
                          <div>
                            <Text
                              variant="span"
                              className="!text-white/90 text-sm font-medium"
                            >
                              Etkinlik {index + 1}
                            </Text>
                          </div>
                        </div>
                        <Text
                          variant="h3"
                          className="!text-white font-bold text-lg mb-2 line-clamp-2"
                        >
                          {activity.baslik}
                        </Text>
                        <div className="flex items-center space-x-3 text-white/90 text-sm">
                          <div className="flex items-center space-x-1">
                            <ClockIcon className="w-4 h-4" />
                            <span>{activity.sure} dk</span>
                          </div>
                          {activity.zorlukSeviyesi && (
                            <span className="bg-white/20 px-2 py-1 rounded text-xs">
                              {activity.zorlukSeviyesi}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <Text
                        variant="p"
                        className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-4"
                      >
                        {activity.aciklama}
                      </Text>

                      {/* UBD Focus Preview */}
                      {activity.ubdOdak && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-4">
                          <Text
                            variant="span"
                            className="text-blue-700 dark:text-blue-300 text-xs font-medium"
                          >
                            🎯 UBD: {activity.ubdOdak.baslik}
                          </Text>
                        </div>
                      )}

                      {/* Quick Stats */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {activity.gerekliMalzemeler &&
                          activity.gerekliMalzemeler.length > 0 && (
                            <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs px-2 py-1 rounded">
                              📋 {activity.gerekliMalzemeler.length} Malzeme
                            </span>
                          )}
                        {activity.senaryolar &&
                          activity.senaryolar.length > 0 && (
                            <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs px-2 py-1 rounded">
                              🎭 {activity.senaryolar.length} Senaryo
                            </span>
                          )}
                        {activity.soruBankası &&
                          activity.soruBankası.length > 0 && (
                            <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs px-2 py-1 rounded">
                              ❓ {activity.soruBankası.length} Soru
                            </span>
                          )}
                      </div>

                      <div className="flex-1" />

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleActivityClick(index)}
                          className="flex-1 flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white"
                          size="sm"
                          rounded="full"
                        >
                          <EyeIcon className="w-4 h-4" />
                          <span>Detaylı Göster</span>
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col">
      {showOverview ? (
        renderOverview()
      ) : (
        <>
          <div className="flex-1 relative overflow-hidden">
            <AnimatePresence mode="wait" custom={slideDirection}>
              <motion.div
                key={currentStep}
                custom={slideDirection}
                variants={slideVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="absolute inset-0 p-6"
              >
                <div className="max-w-6xl mx-auto h-full">
                  <div className="h-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {activities[currentStep] &&
                      renderDynamicStep(activities[currentStep], currentStep)}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-4 bg-white/90 dark:bg-gray-800/90 rounded-full shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-110 hover:shadow-2xl border border-gray-200 dark:border-gray-600"
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>

            <button
              onClick={nextStep}
              disabled={currentStep === activities.length - 1}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-4 bg-white/90 dark:bg-gray-800/90 rounded-full shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-110 hover:shadow-2xl border border-gray-200 dark:border-gray-600"
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ClassicTeachingFlow;
